/**
 * Logger Service
 * Format: TypeIcon | Date & Time | [ <APP_NAME> ] | [ Module ] | Description
 */

// Lazy load config to avoid circular dependency.
// Toggle driven by VITE_CRASH_GAME_LOGGER_STATUS (ON / OFF).
const isLoggerEnabled = () => {
  try {
    const loggerStatus = import.meta.env.VITE_CRASH_GAME_LOGGER_STATUS
    if (loggerStatus === undefined || loggerStatus === null) return true
    return String(loggerStatus).trim().toUpperCase() === 'ON'
  } catch {
    return true
  }
}

const LOG_TYPES = {
  INFO: { icon: 'ℹ️', color: '#3b82f6', label: 'INFO', bgColor: '#dbeafe' },
  SUCCESS: { icon: '✅', color: '#22c55e', label: 'SUCCESS', bgColor: '#dcfce7' },
  WARNING: { icon: '⚠️', color: '#f59e0b', label: 'WARNING', bgColor: '#fef3c7' },
  ERROR: { icon: '❌', color: '#ef4444', label: 'ERROR', bgColor: '#fee2e2' },
  DEBUG: { icon: '🐛', color: '#8b5cf6', label: 'DEBUG', bgColor: '#ede9fe' },
  API: { icon: '🌐', color: '#06b6d4', label: 'API', bgColor: '#cffafe' },
  NETWORK: { icon: '📡', color: '#14b8a6', label: 'NETWORK', bgColor: '#ccfbf1' },
}

const getTimestamp = () => {
  const now = new Date()
  const date = now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const time = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  return `${date} ${time}`
}

const logMessage = (type, module, message, ...args) => {
  if (!isLoggerEnabled()) return

  const logType = LOG_TYPES[type] || LOG_TYPES.INFO
  const timestamp = getTimestamp()

  const styles = {
    icon: `color: ${logType.color}; font-size: 14px;`,
    timestamp: `color: #6b7280; font-weight: normal;`,
    app: `color: #1f2937; font-weight: bold; background: #fbbf24; padding: 2px 6px; border-radius: 3px;`,
    module: `color: #ffffff; font-weight: bold; background: ${logType.color}; padding: 2px 6px; border-radius: 3px;`,
    message: `color: ${logType.color}; font-weight: normal;`,
  }

  console.log(
    `%c${logType.icon}%c | ${timestamp} | %c[ CRASH RENDER ENGINE ]%c | %c[ ${module} ]%c | %c${message}`,
    styles.icon,
    styles.timestamp,
    styles.app,
    styles.timestamp,
    styles.module,
    styles.timestamp,
    styles.message
  )

  if (args.length > 0) console.log(...args)
}

export const logger = {
  info: (module, message, ...args) => logMessage('INFO', module, message, ...args),
  success: (module, message, ...args) => logMessage('SUCCESS', module, message, ...args),
  warn: (module, message, ...args) => logMessage('WARNING', module, message, ...args),
  error: (module, message, ...args) => logMessage('ERROR', module, message, ...args),
  debug: (module, message, ...args) => logMessage('DEBUG', module, message, ...args),
  api: (module, message, ...args) => logMessage('API', module, message, ...args),
  network: (module, message, ...args) => logMessage('NETWORK', module, message, ...args),

  group: (module, label, callback) => {
    if (!isLoggerEnabled()) return
    console.group(`[ ${module} ] ${label}`)
    callback()
    console.groupEnd()
  },

  groupCollapsed: (module, label, callback) => {
    if (!isLoggerEnabled()) return
    console.groupCollapsed(`[ ${module} ] ${label}`)
    callback()
    console.groupEnd()
  },

  table: (module, message, data) => {
    if (!isLoggerEnabled()) return
    logger.info(module, message)
    console.table(data)
  },

  time: (label) => {
    if (isLoggerEnabled()) console.time(label)
  },
  timeEnd: (label) => {
    if (isLoggerEnabled()) console.timeEnd(label)
  },
}

export const log = (...args) => {
  if (isLoggerEnabled()) console.log(...args)
}
export const logError = (...args) => {
  if (isLoggerEnabled()) console.error(...args)
}

export default logger
